import React from 'react';
import { Link } from 'react-router-dom';
import { useStatus } from '../context/StatusContext';
import { isServiceEnabled } from '../api/status';
import { STATUS_LABEL, type Product } from '../data/products';

interface ProductCardProps {
  product: Product;
  /** `row` = compact horizontal card; `tile` = visual tile; `detail` = full write-up. */
  layout?: 'tile' | 'row' | 'detail';
  className?: string;
}

const linkClass =
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary-950';

const ProductCard: React.FC<ProductCardProps> = ({ product, layout = 'tile', className = '' }) => {
  const { services, publicHubStatusEnabled } = useStatus();
  const disabled = publicHubStatusEnabled && !isServiceEnabled(services, product.serviceId);
  const live = product.status === 'active' && !disabled;

  const status = (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium">
      <span
        className={`w-1.5 h-1.5 rounded-full ${disabled ? 'bg-amber-400' : live ? 'bg-emerald-400' : 'bg-amber-500/80'}`}
        aria-hidden="true"
      />
      <span className={disabled ? 'text-amber-300' : live ? 'text-emerald-300' : 'text-secondary-400'}>
        {disabled ? 'Temporarily unavailable' : STATUS_LABEL[product.status]}
      </span>
    </span>
  );

  const action = disabled ? (
    <Link to="/outage" className={`text-sm font-semibold text-amber-300 hover:text-amber-200 rounded ${linkClass}`}>
      View outage info →
    </Link>
  ) : product.external ? (
    <a
      href={product.link}
      target="_blank"
      rel="noopener noreferrer"
      className={`text-sm font-semibold text-primary-300 hover:text-primary-200 rounded ${linkClass}`}
    >
      {product.cta} ↗<span className="sr-only"> (opens in a new tab)</span>
    </a>
  ) : (
    <Link to={product.link} className={`text-sm font-semibold text-primary-300 hover:text-primary-200 rounded ${linkClass}`}>
      {product.cta} →
    </Link>
  );

  const frame = 'rounded-2xl border border-secondary-700/60 bg-secondary-900 hover:border-primary-500/40 transition-colors';

  const details = product.details.length > 0 && (
    <div className="mt-3 space-y-2">
      {product.details.map((paragraph) => (
        <p key={paragraph} className="text-secondary-300 text-sm leading-relaxed">{paragraph}</p>
      ))}
    </div>
  );

  if (layout === 'detail') {
    return (
      <article className={`${frame} p-5 sm:p-6 ${className}`}>
        <div className="flex items-start gap-4">
          <div className="w-16 h-16 shrink-0 rounded-xl bg-secondary-800 border border-secondary-700 flex items-center justify-center">
            <img src={product.logo} alt="" className="w-10 h-10 object-contain" loading="lazy" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <h3 className="font-semibold text-white text-lg leading-tight">{product.name}</h3>
              {status}
            </div>
            <p className="text-secondary-200 text-sm leading-relaxed mt-2">{product.summary}</p>
          </div>
        </div>
        {details}
        <div className="mt-4 min-h-[28px] flex items-center">{action}</div>
      </article>
    );
  }

  if (layout === 'row') {
    return (
      <article className={`${frame} flex items-center gap-4 p-4 ${className}`}>
        <div className="w-16 h-16 shrink-0 rounded-xl bg-secondary-800 border border-secondary-700 flex items-center justify-center">
          <img src={product.logo} alt="" className="w-10 h-10 object-contain" loading="lazy" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-white leading-tight">{product.name}</h3>
          <div className="mt-1">{status}</div>
          <p className="text-secondary-400 text-sm leading-snug mt-1.5">{product.summary}</p>
          <div className="mt-2 min-h-[28px] flex items-center">{action}</div>
        </div>
      </article>
    );
  }

  return (
    <article className={`${frame} overflow-hidden flex flex-col h-full ${className}`}>
      <div
        className="h-28 sm:h-32 flex items-center justify-center border-b border-secondary-800/70"
        style={{ background: 'radial-gradient(ellipse 70% 90% at 50% 50%, rgba(14,165,233,0.14) 0%, rgba(2,6,23,0) 70%), #0b1224' }}
      >
        <img src={product.logo} alt="" className="w-14 h-14 sm:w-16 sm:h-16 object-contain" loading="lazy" />
      </div>
      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-semibold text-white leading-tight">{product.name}</h3>
          <div className="shrink-0 pt-0.5">{status}</div>
        </div>
        <p className="text-secondary-400 text-sm leading-snug mt-2 flex-1">{product.summary}</p>
        <div className="mt-3 min-h-[28px] flex items-center">{action}</div>
      </div>
    </article>
  );
};

export default ProductCard;
